import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, auth } from '../api/client';
import type { UserRole } from '../types/models';

function homeFor(role: UserRole | undefined) {
  if (role === 'Admin') return '/platform/organizations';
  if (role === 'Staff') {
    // Staff work inside their org workspace (data is scoped to their institution by the API).
    const { organizationId } = auth.getPermissions();
    return organizationId ? `/org/${organizationId}/institutions` : '/staff';
  }
  return '/exams';
}

function EyeIcon({ off }: { off: boolean }) {
  return off ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function PasswordField({ label, value, onChange, hint, autoComplete }: {
  label: string; value: string; onChange: (v: string) => void; hint?: string; autoComplete?: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <label>
      <div className="muted">{label}</div>
      <div className="password-field">
        <input type={show ? 'text' : 'password'} value={value} autoComplete={autoComplete}
               onChange={e => onChange(e.target.value)} required />
        <button type="button" className="password-toggle eye"
                onClick={() => setShow(s => !s)} aria-label={show ? 'Hide password' : 'Show password'}>
          <EyeIcon off={show} />
        </button>
      </div>
      {hint && <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{hint}</div>}
    </label>
  );
}

export function ChangePasswordPage() {
  const nav = useNavigate();
  const user = auth.getUser();
  const forced = auth.getMustChangePassword();
  const firstName = (user?.fullName ?? '').trim().split(' ')[0] || 'there';

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (next.length < 8) { setError('New password must be at least 8 characters.'); return; }
    if (next !== confirm) { setError('New password and confirmation do not match.'); return; }
    if (next === current) { setError('New password must be different from the current one.'); return; }
    setBusy(true);
    try {
      await api.changePassword(current, next);
      auth.setMustChangePassword(false);
      nav(homeFor(user?.role), { replace: true });
    } catch (e: any) {
      setError(e?.message ?? 'Failed to change password.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="cp-page">
      <header className="cp-header">
        <span className="muted">Welcome back, {firstName}</span>
        {!forced && (
          <button className="link-btn" onClick={() => nav(homeFor(user?.role), { replace: true })}>Cancel</button>
        )}
      </header>

      <div className="cp-body">
        <h1 style={{ marginTop: 0 }}>Change Password</h1>
        {forced && <div className="cp-warn">You must change your password before continuing.</div>}

        <form onSubmit={submit} className="card cp-card col">
          <PasswordField label="Current password" value={current} onChange={setCurrent}
                         autoComplete="current-password" />
          <PasswordField label="New password" value={next} onChange={setNext}
                         autoComplete="new-password" hint="Minimum 8 characters." />
          <PasswordField label="Confirm new password" value={confirm} onChange={setConfirm}
                         autoComplete="new-password" />
          {error && <div className="error">{error}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="primary" disabled={busy} type="submit">
              {busy ? 'Saving…' : 'Change password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
